import { registerPlugin, Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

interface NativeVibratorPluginInterface {
  performHaptic(options: { type: string }): Promise<{ performed?: boolean; error?: string }>;
  vibrate(options: { duration: number }): Promise<{ vibrated?: boolean; hasVibrator?: boolean; error?: string }>;
  vibratePattern(options: { pattern: number[] }): Promise<{ vibrated?: boolean; hasVibrator?: boolean; error?: string }>;
}

const NativeVibrator = registerPlugin<NativeVibratorPluginInterface>('NativeVibrator');

/**
 * Subtle tick for countdowns (e.g. Blitz 10s countdown, Versus 5s buzzer countdown).
 * Crisp and gentle.
 */
export async function vibrateTick(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      try {
        const res = await NativeVibrator.performHaptic({ type: 'clock_tick' });
        if (res && res.performed) return;
      } catch {
        // Fall back to Capacitor Haptics
      }

      try {
        await Haptics.impact({ style: ImpactStyle.Light });
        return;
      } catch {
        // Fall back to direct vibrate
      }

      try {
        await NativeVibrator.vibrate({ duration: 25 });
        return;
      } catch {
        // Fall back to web
      }
    }

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(25);
    }
  } catch (e) {
    // Ignore haptic errors on unsupported hardware
  }
}

/**
 * Subtle feedback for buzzer presses or button confirmations.
 */
export async function vibrateSubtle(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      try {
        const res = await NativeVibrator.performHaptic({ type: 'confirm' });
        if (res && res.performed) return;
      } catch {
        // Fall back to Capacitor Haptics
      }

      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
        return;
      } catch {
        // Fall back to direct vibrate
      }

      try {
        await NativeVibrator.vibrate({ duration: 50 });
        return;
      } catch {
        // Fall back to web
      }
    }

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(40);
    }
  } catch (e) {
    // Ignore haptic errors on unsupported hardware
  }
}

/**
 * Distinct vibration burst for penalties (e.g. buzzer timeout, invalid set).
 */
export async function vibrateBurst(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      try {
        await NativeVibrator.vibratePattern({ pattern: [0, 80, 50, 80] });
        return;
      } catch {
        // Fall back to Capacitor Haptics
      }

      try {
        await Haptics.notification({ type: NotificationType.Error });
        return;
      } catch {
        // Fall back to direct vibrate
      }

      try {
        await NativeVibrator.vibrate({ duration: 150 });
        return;
      } catch {
        // Fall back to web
      }
    }

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([80, 50, 80]);
    }
  } catch (e) {
    // Ignore haptic errors on unsupported hardware
  }
}

